import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Phone, 
  MapPin, 
  Clock, 
  RefreshCw, 
  Loader2,
  ThumbsUp,
  ThumbsDown,
  Navigation,
  AlertCircle,
  CheckCircle2,
  Wallet,
  AlertTriangle,
  Info,
  ShoppingCart,
  Mail,
  ExternalLink,
  ArrowLeft
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TipsList component to render coaching tips
function TipsList({ tips }) {
  if (!tips || tips.length === 0) {
    return (
      <p className="text-slate-500 text-center py-4">
        Ingen råd tilgængelige. Tryk "Opdater" for at generere.
      </p>
    );
  }
  
  const items = [];
  for (let i = 0; i < tips.length; i++) {
    const tip = tips[i];
    
    // Handle different tip formats
    if (typeof tip === 'string') {
      // Simple string tip
      items.push(
        <div 
          key={i} 
          className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0"
          data-testid={`tip-${i + 1}`}
        >
          <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-slate-900 text-white text-sm font-bold">
            {i + 1}
          </span>
          <p className="text-slate-700 leading-relaxed text-sm">{tip}</p>
        </div>
      );
    } else if (tip && typeof tip === 'object') {
      // Rich tip with title, do, say, avoid, why
      const hasRichContent = tip.do || tip.say || tip.avoid;
      
      items.push(
        <div 
          key={i} 
          className="pb-4 border-b border-slate-100 last:border-0"
          data-testid={`tip-${i + 1}`}
        >
          <div className="flex items-start gap-3 mb-2">
            <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-slate-900 text-white text-sm font-bold">
              {i + 1}
            </span>
            <p className="text-slate-900 font-medium">{tip.title || tip.tip || ''}</p>
          </div>
          
          {hasRichContent && (
            <div className="ml-9 space-y-2 text-sm">
              {tip.do && (
                <div className="flex items-start gap-2">
                  <span className="text-emerald-600 font-medium shrink-0">Gør:</span>
                  <span className="text-slate-600">{tip.do}</span>
                </div>
              )}
              {tip.say && (
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-medium shrink-0">Sig:</span>
                  <span className="text-slate-600 italic">"{tip.say}"</span>
                </div>
              )}
              {tip.avoid && (
                <div className="flex items-start gap-2">
                  <span className="text-red-600 font-medium shrink-0">Undgå:</span>
                  <span className="text-slate-600">{tip.avoid}</span>
                </div>
              )}
              {tip.why && (
                <div className="flex items-start gap-2">
                  <span className="text-slate-400 font-medium shrink-0">Hvorfor:</span>
                  <span className="text-slate-500">{tip.why}</span>
                </div>
              )}
            </div>
          )}
          
          {!hasRichContent && tip.tip && (
            <p className="ml-9 text-slate-600 text-sm">{tip.tip}</p>
          )}
        </div>
      );
    }
  }
  
  return <div className="space-y-3">{items}</div>;
}

const PrepPage = () => {
  const { appointmentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token: jwtToken } = useAuth();  // Get JWT token from auth context
  const urlToken = searchParams.get('t');  // Get token from URL if present

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [showFeedbackComment, setShowFeedbackComment] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState('');

  // Helper to get auth headers
  const getAuthConfig = () => {
    if (jwtToken) {
      return { headers: { Authorization: `Bearer ${jwtToken}` } };
    }
    return {};
  };

  // Helper to get URL auth param
  const getAuthParam = () => {
    return urlToken ? `?t=${urlToken}` : '';
  };

  useEffect(() => {
    fetchPrepData();
  }, [appointmentId, urlToken, jwtToken]);

  const fetchPrepData = async () => {
    // Need either JWT token or URL token
    if (!jwtToken && !urlToken) {
      setError('Manglende adgangstoken');
      setLoading(false);
      return;
    }

    try {
      const url = `${API}/prep/${appointmentId}${getAuthParam()}`;
      const response = await axios.get(url, getAuthConfig());
      setData(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunne ikke hente data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const url = `${API}/prep/${appointmentId}/refresh${getAuthParam()}`;
      const response = await axios.post(url, {}, getAuthConfig());
      if (response.data.coaching) {
        setData(prev => ({
          ...prev,
          coaching: response.data.coaching
        }));
      }
    } catch (err) {
      setError('Kunne ikke opdatere råd');
    } finally {
      setRefreshing(false);
    }
  };

  const handleFeedback = async (useful) => {
    if (!useful) {
      setShowFeedbackComment(true);
      return;
    }

    try {
      const url = `${API}/prep/${appointmentId}/feedback${getAuthParam()}`;
      await axios.post(url, { useful: true, comment: '' }, getAuthConfig());
      setFeedbackSent(true);
    } catch (err) {
      console.error('Feedback error:', err);
    }
  };

  const submitFeedbackWithComment = async () => {
    try {
      const url = `${API}/prep/${appointmentId}/feedback${getAuthParam()}`;
      await axios.post(url, {
        useful: false,
        comment: feedbackComment
      }, getAuthConfig());
      setFeedbackSent(true);
      setShowFeedbackComment(false);
    } catch (err) {
      console.error('Feedback error:', err);
    }
  };

  const formatTime = (isoString) => {
    try {
      const dt = new Date(isoString);
      return dt.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const formatArrival = (minutes) => {
    if (minutes === null || minutes === undefined) return null;
    if (minutes < 1) return 'Nu';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}t ${mins}m`;
  };

  const getGoogleMapsUrl = (address, city, postalCode) => {
    const fullAddress = [address, postalCode, city].filter(Boolean).join(', ');
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-slate-600 mx-auto mb-2" />
          <p className="text-slate-500">Henter forberedelse...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-slate-900 mb-2">Kunne ikke hente data</h2>
            <p className="text-slate-500 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Prøv igen
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { appointment, coaching, user, survey } = data || {};

  // Helper to get avoid preference text
  const getAvoidText = (value) => {
    const map = {
      'for_lang_tid': 'At det tager for lang tid',
      'foele_presset': 'At føle sig presset',
      'forvirret_valg': 'For mange valg',
      'mangle_overblik': 'Mangle overblik over pris'
    };
    return map[value] || value;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header - with safe area for iOS PWA */}
      <div className="bg-slate-900 text-white px-4 py-4 sticky top-0 z-10 safe-top">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="text-white hover:bg-slate-800"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold font-outfit">Forbered besøg</h1>
            {user?.name && <p className="text-slate-400 text-sm">{user.name}</p>}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4 max-w-lg mx-auto pb-24">
        {/* Appointment Card */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="pt-4">
            {/* Customer & Time */}
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  {appointment?.customer_name || 'Kunde'}
                </h2>
                {(appointment?.address || appointment?.city) && (
                  <p className="text-slate-500 text-sm flex items-center gap-1 mt-1">
                    <MapPin className="h-3 w-3" />
                    {[appointment?.address, appointment?.postal_code, appointment?.city].filter(Boolean).join(', ')}
                  </p>
                )}
              </div>
              
              <div className="text-right">
                <p className="text-xl font-bold text-slate-900">
                  {formatTime(appointment?.scheduled_start)}
                </p>
                {appointment?.arrival_minutes !== null && (
                  <Badge className="mt-1 bg-teal-100 text-teal-700 border-0">
                    <Clock className="h-3 w-3 mr-1" />
                    {formatArrival(appointment?.arrival_minutes)}
                  </Badge>
                )}
              </div>
            </div>
            
            {/* Booking type badge */}
            {appointment?.booking_type && (
              <Badge variant="outline" className="mb-3 capitalize">
                {appointment.booking_type}
              </Badge>
            )}

            {/* Contact info row */}
            <div className="flex flex-wrap gap-3 mb-4 text-sm">
              {appointment?.customer_phone && (
                <a 
                  href={`tel:${appointment.customer_phone}`}
                  className="flex items-center gap-1.5 text-slate-600 hover:text-teal-700"
                >
                  <Phone className="h-4 w-4" />
                  <span>{appointment.customer_phone}</span>
                </a>
              )}
              {appointment?.customer_email && (
                <a 
                  href={`mailto:${appointment.customer_email}`}
                  className="flex items-center gap-1.5 text-slate-600 hover:text-teal-700"
                >
                  <Mail className="h-4 w-4" />
                  <span className="truncate max-w-[180px]">{appointment.customer_email}</span>
                </a>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              {appointment?.customer_phone && (
                <Button
                  asChild
                  variant="outline"
                  className="flex-1 h-12"
                  data-testid="call-customer-btn"
                >
                  <a href={`tel:${appointment.customer_phone}`}>
                    <Phone className="h-4 w-4 mr-2" />
                    Ring op
                  </a>
                </Button>
              )}
              
              {appointment?.address && (
                <Button
                  asChild
                  className="flex-1 h-12 bg-slate-900 hover:bg-slate-800"
                  data-testid="open-maps-btn"
                >
                  <a 
                    href={getGoogleMapsUrl(appointment.address, appointment.city, appointment.postal_code)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Navigation className="h-4 w-4 mr-2" />
                    Åbn rute
                  </a>
                </Button>
              )}
            </div>
            
            {/* Magento links */}
            {(appointment?.asset_processing_url || appointment?.conversion_magento_customer_url) && (
              <div className="flex gap-2 mt-3">
                {appointment?.asset_processing_url && (
                  <Button
                    asChild
                    variant="outline"
                    className="flex-1 h-10 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                    data-testid="asset-order-btn"
                  >
                    <a 
                      href={appointment.asset_processing_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      Ordre i ASSET
                    </a>
                  </Button>
                )}
                {appointment?.conversion_magento_customer_url && (
                  <Button
                    asChild
                    variant="outline"
                    className="flex-1 h-10 bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
                    data-testid="magento-customer-btn"
                  >
                    <a 
                      href={appointment.conversion_magento_customer_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Kunde i ASSET
                    </a>
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Survey Info Card - if customer filled out survey */}
        {survey && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2 bg-teal-50 rounded-t-lg border-b border-teal-100">
              <CardTitle className="text-teal-900 text-base font-medium flex items-center gap-2">
                <Info className="h-4 w-4" />
                Fra kundens forhåndssvar
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              {/* Budget */}
              {survey.has_budget && (
                <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                  <Wallet className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-emerald-900">Budget</p>
                    <p className="text-emerald-700 text-sm">{survey.budget}</p>
                  </div>
                </div>
              )}
              
              {/* Avoid preference */}
              {survey.avoid_preference && (
                <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-amber-900">Vil gerne undgå</p>
                    <p className="text-amber-700 text-sm">{getAvoidText(survey.avoid_preference)}</p>
                  </div>
                </div>
              )}
              
              {/* Special notes */}
              {survey.special_notes && (
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <Info className="h-5 w-5 text-slate-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-slate-900">Særlige hensyn</p>
                    <p className="text-slate-600 text-sm">{survey.special_notes}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Coaching Card */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2 bg-slate-900 rounded-t-lg">
            <CardTitle className="text-white text-base font-medium">
              {coaching?.heading || 'Det her skal du især være opmærksom på'}
            </CardTitle>
            {coaching?.subheading && (
              <p className="text-slate-400 text-sm mt-1">{coaching.subheading}</p>
            )}
          </CardHeader>
          
          <CardContent className="pt-4">
            <TipsList tips={coaching?.tips} />

            {/* Refresh Button */}
            <Button
              onClick={handleRefresh}
              disabled={refreshing}
              variant="outline"
              className="w-full mt-4"
              data-testid="refresh-coaching-btn"
            >
              {refreshing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Opdaterer...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Opdater / hent nyeste råd
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Feedback Section */}
        {!feedbackSent ? (
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="pt-4">
              {!showFeedbackComment ? (
                <>
                  <p className="text-slate-600 text-sm text-center mb-3">
                    Var rådene brugbare?
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleFeedback(true)}
                      variant="outline"
                      className="flex-1 h-12 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      data-testid="feedback-yes-btn"
                    >
                      <ThumbsUp className="h-4 w-4 mr-2" />
                      Ja
                    </Button>
                    <Button
                      onClick={() => handleFeedback(false)}
                      variant="outline"
                      className="flex-1 h-12 border-slate-200 text-slate-600 hover:bg-slate-50"
                      data-testid="feedback-no-btn"
                    >
                      <ThumbsDown className="h-4 w-4 mr-2" />
                      Nej
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-slate-600 text-sm mb-2">
                    Hvad kunne være bedre? (valgfrit)
                  </p>
                  <Textarea
                    value={feedbackComment}
                    onChange={(e) => setFeedbackComment(e.target.value)}
                    placeholder="Skriv din kommentar..."
                    className="mb-3"
                    rows={2}
                    data-testid="feedback-comment-input"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowFeedbackComment(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Annuller
                    </Button>
                    <Button
                      onClick={submitFeedbackWithComment}
                      className="flex-1 bg-slate-900"
                      data-testid="submit-feedback-btn"
                    >
                      Send feedback
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-emerald-100 bg-emerald-50">
            <CardContent className="pt-4 text-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
              <p className="text-emerald-700 font-medium">Tak for din feedback!</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PrepPage;
